// src/utils/firebase/fetchMeasurementVariantsPresence.js
import { collection, getDocs, limit, query, where } from "firebase/firestore";

/**
 * @typedef {object} MeasurementVariantPresenceResult
 * @property {Object<string, boolean>} byVariant
 *   Map of measurement variant slug to whether any matching document exists.
 * @property {boolean} any
 *   `true` when at least one requested variant has data.
 */

/**
 * Checks, for each variant, whether the user has at least one measurement doc.
 *
 * Assumed structure:
 *   measurements/{uid}/{variant}/{measurementId}
 *
 * @param {object} args
 * @param {object} args.db
 *   Firestore database instance.
 * @param {string} args.uid
 * @param {string[]} args.variants
 * @param {boolean} [args.requireCompleted=false] - if true, filters for status == "completed"
 * @returns {Promise<MeasurementVariantPresenceResult>}
 */
export async function fetchMeasurementVariantsPresence({
  db,
  uid,
  variants,
  requireCompleted = false,
}) {
  if (!db) throw new Error("fetchMeasurementVariantsPresence: db is required");
  if (!uid)
    throw new Error("fetchMeasurementVariantsPresence: uid is required");
  if (!Array.isArray(variants) || variants.length === 0)
    throw new Error("fetchMeasurementVariantsPresence: variants[] is required");

  const checks = await Promise.all(
    variants.map(async (variant) => {
      const base = collection(db, "measurements", uid, variant);

      const q = requireCompleted
        ? query(base, where("status", "==", "completed"), limit(1))
        : query(base, limit(1));

      const snap = await getDocs(q);
      return [variant, snap.size > 0];
    }),
  );

  const byVariant = Object.fromEntries(checks);
  const any = Object.values(byVariant).some(Boolean);

  return { byVariant, any };
}
